import { useState } from "react"

import {
  Button,
  FormActions,
  FormField,
  FormFieldset,
  Input,
  Loader,
} from "@coval/ui"

const NAME_MAX_LENGTH = 200

export interface WorkspaceCreateDraft {
  displayName: string
}

interface WorkspaceCreateFormProps {
  error: string
  isPending: boolean
  onCancel: () => void
  onSubmit: (draft: WorkspaceCreateDraft) => void
  /** Clears the submit error as the viewer edits the name. */
  onDirty: () => void
}

/** Validates a new workspace's name before handing it to the mutation owner. */
export function WorkspaceCreateForm({
  error,
  isPending,
  onCancel,
  onDirty,
  onSubmit,
}: WorkspaceCreateFormProps) {
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState("")

  const handleSubmit = () => {
    const trimmedName = name.trim()
    setNameError("")

    if (!trimmedName || trimmedName.length > NAME_MAX_LENGTH) {
      setNameError(
        `Workspace name must be between 1 and ${NAME_MAX_LENGTH} characters.`,
      )
      return
    }

    onSubmit({ displayName: trimmedName })
  }

  return (
    <form
      data-testid="workspace-create-form"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <FormFieldset>
        <FormField label="Workspace name" error={nameError}>
          {(ids) => (
            <Input
              {...ids}
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameError("")
                onDirty()
              }}
              disabled={isPending}
              error={Boolean(nameError)}
              data-testid="workspace-create-name"
            />
          )}
        </FormField>

        {error && (
          <div role="alert" className="text-xs text-coval-danger-text">
            {error}
          </div>
        )}
      </FormFieldset>

      <FormActions>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isPending}
          data-testid="workspace-create-confirm"
        >
          {isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader size="sm" />
              Creating...
            </span>
          ) : (
            "Create workspace"
          )}
        </Button>
      </FormActions>
    </form>
  )
}
